import React, { useState, useEffect } from 'react';
import {
  Dialog, Box, Typography, Button, IconButton, Chip, CircularProgress,
} from '@mui/material';

import {
  Close as CloseIcon, Print as PrintIcon,
  FlipToBack as FlipToBackIcon, FlipToFront as FlipToFrontIcon,
} from '@mui/icons-material';


import { companyInfo as companyInfoURL } from '../BaseURL';

import Swal from 'sweetalert2';


const GET_CARD_COLORS_URL       = companyInfoURL.replace('companyInfo', 'getCardColors');
const SAVE_CARD_COLORS_URL      = companyInfoURL.replace('companyInfo', 'saveCardColors');
const GET_INSURANCE_URL         = companyInfoURL.replace('companyInfo', 'getInsurancePartners');
const GET_ID_CARD_AMOUNT_URL    = companyInfoURL.replace('companyInfo', 'getIdCardAmount');
const REQUEST_SERVICE_URL       = companyInfoURL.replace('companyInfo', 'requestOtherService');
const CHECK_ID_CARD_CHARGE_URL  = companyInfoURL.replace('companyInfo', 'checkIdCardCharge');

// Landscape CR80: 85.6 × 54 mm — preview at ~8px/mm
const CW = 685;
const CH = 432;

const FALLBACK_PRIMARY = '#0d6e8a';
const FALLBACK_ACCENT  = '#1a9b7f';

const SERVICES = [
  ['General OPD',           'Dental Care'],
  ['In-Patients/VIP Wards', 'Obs & Gynae / Fertility Clinic'],
  ['Maternal Health Care',  'Ear, Nose & Throat'],
  ['Surgeries',             'Eye Clinic'],
  ['Dental X-rays',         'Mental Health'],
  ['Scans',                 'Diabetes / Hypertensive Clinic'],
  ['ECG',                   'Laboratory Diagnostics'],
];

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const rgbHex = (r,g,b) => '#'+[r,g,b].map(v=>v.toString(16).padStart(2,'0')).join('');
function shiftColor(hex,amt){
  const r=parseInt(hex.slice(1,3),16),g=parseInt(hex.slice(3,5),16),b=parseInt(hex.slice(5,7),16);
  return rgbHex(
    Math.min(255,Math.max(0,Math.round(r+(amt>0?(255-r):r)*Math.abs(amt)))),
    Math.min(255,Math.max(0,Math.round(g+(amt>0?(255-g):g)*Math.abs(amt)))),
    Math.min(255,Math.max(0,Math.round(b+(amt>0?(255-b):b)*Math.abs(amt)))),
  );
}
const darken  = (hex,amt=0.2) => shiftColor(hex,-amt);
const lighten = (hex,amt=0.3) => shiftColor(hex, amt);
const alpha = (hex,a) => {
  const r=parseInt(hex.slice(1,3),16),g=parseInt(hex.slice(3,5),16),b=parseInt(hex.slice(5,7),16);
  return `rgba(${r},${g},${b},${a})`;
};

// ─────────────────────────────────────────────────────────────────────────────
// COLOR EXTRACTION
// ─────────────────────────────────────────────────────────────────────────────
const extractColorsFromImage = (src) =>
  new Promise((resolve) => {
    if (!src) return resolve({ primary: FALLBACK_PRIMARY, accent: FALLBACK_ACCENT });
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const SIZE = 64;
        const canvas = document.createElement('canvas');
        canvas.width = SIZE; canvas.height = SIZE;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, SIZE, SIZE);
        const { data } = ctx.getImageData(0, 0, SIZE, SIZE);
        const buckets = {};
        for (let i = 0; i < data.length; i += 4) {
          const r=data[i],g=data[i+1],b=data[i+2],a=data[i+3];
          if (a<128||(r>235&&g>235&&b>235)||(r<25&&g<25&&b<25)) continue;
          const max=Math.max(r,g,b),min=Math.min(r,g,b);
          if (max===0||(max-min)/max<0.18) continue;
          const key=`${Math.round(r/16)*16},${Math.round(g/16)*16},${Math.round(b/16)*16}`;
          buckets[key]=(buckets[key]||0)+1;
        }
        const sorted=Object.entries(buckets).sort((a,b)=>b[1]-a[1]);
        if (!sorted.length) return resolve({ primary: FALLBACK_PRIMARY, accent: FALLBACK_ACCENT });
        const [rp,gp,bp]=sorted[0][0].split(',').map(Number);
        const primary=rgbHex(rp,gp,bp);
        let accent=null;
        for (let j=1;j<sorted.length;j++){
          const [ra,ga,ba]=sorted[j][0].split(',').map(Number);
          if (Math.abs(ra-rp)+Math.abs(ga-gp)+Math.abs(ba-bp)>40){accent=rgbHex(ra,ga,ba);break;}
        }
        if (!accent) accent=rgbHex(Math.min(255,Math.round(rp*0.6+255*0.4)),Math.min(255,Math.round(gp*0.6+255*0.4)),Math.min(255,Math.round(bp*0.6+255*0.4)));
        resolve({ primary, accent });
      } catch { resolve({ primary: FALLBACK_PRIMARY, accent: FALLBACK_ACCENT }); }
    };
    img.onerror = () => resolve({ primary: FALLBACK_PRIMARY, accent: FALLBACK_ACCENT });
    img.src = src;
  });

// ─────────────────────────────────────────────────────────────────────────────
// INFO ROW — used in CardFront right column
// ─────────────────────────────────────────────────────────────────────────────
const InfoRow = ({ label, value, primary, multiline = false }) => (
  <Box>
    <Typography sx={{
      fontSize: 9, fontWeight: 800, color: alpha(primary, 0.85),
      textTransform: 'uppercase', letterSpacing: 1.2, lineHeight: 1,
    }}>
      {label}
    </Typography>
    <Typography sx={{
      fontSize: multiline ? 13 : 14,
      fontWeight: 800, color: '#1a1a2e', lineHeight: 1.25, mt: 0.3,
      ...(multiline ? {
        overflow: 'hidden', display: '-webkit-box',
        WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
      } : {
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }),
    }}>
      {value || 'N/A'}
    </Typography>
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// CARD FRONT
// ─────────────────────────────────────────────────────────────────────────────
const CardFront = ({ patient, companyInfo, primary, accent, insurancePartners = [] }) => {
  const dark    = darken(primary, 0.30);
  const mid     = lighten(primary, 0.15);
  const bgCard  = '#f8fafc';

  const HEADER_H = 70;  // px — reduced to give more content room
  const FOOTER_H = 18;  // px — slimmer footer

  const initials = (patient?.fullName || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  const website = (companyInfo.website || `www.${(companyInfo.name||'hospital').toLowerCase().replace(/\s+/g,'')}hospital.com`).replace(/https?:\/\//,'');

  return (
    <Box sx={{
      width: CW, height: CH,
      position: 'relative', overflow: 'hidden',
      borderRadius: '14px',
      background: bgCard,
      fontFamily: '"Segoe UI", "Helvetica Neue", Arial, sans-serif',
    }}>
      {/* ── Subtle background grid ── */}
      <Box sx={{
        position: 'absolute', inset: 0, zIndex: 0, opacity: 0.035,
        backgroundImage: `repeating-linear-gradient(0deg, ${primary} 0, ${primary} 1px, transparent 1px, transparent 28px),
                          repeating-linear-gradient(90deg, ${primary} 0, ${primary} 1px, transparent 1px, transparent 28px)`,
      }}/>

      {/* ── HEADER ── */}
      <Box sx={{
        position: 'absolute', top: 0, left: 0, right: 0, height: HEADER_H, zIndex: 4,
        background: `linear-gradient(130deg, ${dark} 0%, ${primary} 55%, ${mid} 100%)`,
        overflow: 'hidden',
      }}>
        {/* Diagonal decorative stripes */}
        {[0,1,2,3,4,5].map(i => (
          <Box key={i} sx={{
            position: 'absolute', top: -40, left: `${50 + i * 50}px`,
            width: '2px', height: '200px',
            background: 'rgba(255,255,255,0.06)',
            transform: 'rotate(25deg)',
          }}/>
        ))}

        {/* Left: Initials + Hospital info */}
        <Box sx={{
          position: 'absolute', left: 18, top: 0, bottom: 0,
          display: 'flex', alignItems: 'center', gap: 1.8,
        }}>
          {/* Initials avatar */}
          <Box sx={{
            width: 46, height: 46, borderRadius: '50%', flexShrink: 0,
            background: 'rgba(255,255,255,0.18)',
            border: '2px solid rgba(255,255,255,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Typography sx={{ fontSize: 16, fontWeight: 900, color: 'white', letterSpacing: -0.5, lineHeight: 1, userSelect: 'none' }}>
              {initials}
            </Typography>
          </Box>
          {/* Hospital name */}
          <Box>
            <Typography sx={{ fontSize: 24, fontWeight: 800, color: 'white', lineHeight: 1.2, letterSpacing: 0.2 }}>
              {companyInfo.name || 'HOSPITAL'}
            </Typography>
            <Typography sx={{ fontSize: 8, color: 'rgba(255,255,255,0.72)', letterSpacing: 1.8, textTransform: 'uppercase', mt: 0.3 }}>
              {companyInfo.mmda || 'Quality Healthcare'}
            </Typography>
          </Box>
        </Box>

        {/* Right: Logo — fills the area previously taken by the white circle */}
        <Box sx={{
          position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
          width: 62, height: 62,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 6,
        }}>
          {companyInfo.logo ? (
            <Box component="img" src={companyInfo.logo} sx={{ width: '100%', height: '100%', objectFit: 'contain' }}/>
          ) : (
            <Typography sx={{ fontSize: 30, fontWeight: 900, color: 'white', lineHeight: 1 }}>+</Typography>
          )}
        </Box>
      </Box>

      {/* ── CONTENT AREA ── */}
      <Box sx={{
        position: 'absolute',
        top: HEADER_H + 6,
        left: 0, right: 0,
        bottom: FOOTER_H + 4,
        display: 'flex',
        px: 2.5,
        gap: 2,
        zIndex: 2,
      }}>
        {/* ── LEFT COLUMN ── */}
        <Box sx={{ width: 290, display: 'flex', flexDirection: 'column' }}>
          {/* Patient name */}
          <Typography sx={{
            fontSize: 18, fontWeight: 900, color: dark,
            textTransform: 'uppercase', letterSpacing: 0.3, lineHeight: 1.05, mb: 0.1,
          }}>
            {patient.fullName}
          </Typography>

          {/* PATIENT label */}
          <Typography sx={{ fontSize: 7, color: accent, fontWeight: 800, letterSpacing: 4, textTransform: 'uppercase', mb: 0.3 }}>
            P A T I E N T
          </Typography>

          {/* Accent rule */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.4 }}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: primary, flexShrink: 0 }}/>
            <Box sx={{ flex: 1, height: 2, borderRadius: 4, background: `linear-gradient(90deg,${primary},${alpha(accent,0.18)})` }}/>
          </Box>

          {/* ID Badge */}
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.8,
            px: 1.8, py: 0.55, borderRadius: 1.5, mb: 0.6,
            background: `linear-gradient(130deg, ${dark} 0%, ${primary} 100%)`,
            width: 'fit-content',
            boxShadow: `0 3px 10px ${alpha(primary, 0.42)}`,
          }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: 'rgba(255,255,255,0.55)' }}/>
            <Typography sx={{ fontSize: 14, fontWeight: 900, color: 'white', letterSpacing: 1.8, textTransform: 'uppercase' }}>
              ID: {String(patient.id)}
            </Typography>
          </Box>

          {/* Insurance Partners */}
          {insurancePartners.length > 0 && (
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                <Box sx={{ width: 12, height: 1.5, background: primary, borderRadius: 1 }}/>
                <Typography sx={{ fontSize: 7, color: primary, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 1.2 }}>
                  Insurance Partners
                </Typography>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0px 4px' }}>
                {insurancePartners.slice(0, 20).map((p, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, py: '1px' }}>
                    <Box sx={{
                      width: 4, height: 4, borderRadius: '50%', flexShrink: 0,
                      background: i % 2 === 0 ? primary : accent,
                    }}/>
                    <Typography sx={{ fontSize: 10, color: '#2a2a3e', fontWeight: 700, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.name}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>

        {/* ── VERTICAL DIVIDER ── */}
        <Box sx={{
          width: '1px', alignSelf: 'stretch',
          background: `linear-gradient(180deg, transparent, ${alpha(primary, 0.18)} 30%, ${alpha(primary, 0.18)} 70%, transparent)`,
          flexShrink: 0, my: 0.5,
        }}/>

        {/* ── RIGHT COLUMN ── */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', pt: 0.5, pb: 3 }}>
          <InfoRow label="Phone"        value={patient.tel || companyInfo.phone || '—'} primary={primary}/>
          <InfoRow label="Insurance No" value={patient.Insurance || 'N/A'} primary={primary}/>
          <InfoRow label="Gender"       value={patient.gender || patient.sex || 'N/A'} primary={primary}/>
          <InfoRow label="Address"      value={patient.address || patient.location || companyInfo.location || 'N/A'} primary={primary} multiline/>
        </Box>
      </Box>

      {/* ── EMAIL FOOTER ── */}
      <Box sx={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: FOOTER_H, zIndex: 4,
        background: `linear-gradient(130deg, ${dark} 0%, ${primary} 55%, ${mid} 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5,
      }}>
        <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.9)', fontWeight: 700, letterSpacing: 0.3 }}>
          ✉ {companyInfo.email || `info@${(companyInfo.name||'hospital').toLowerCase().replace(/\s+/g,'')}hospital.com`}
        </Typography>
        <Box sx={{ width: 3, height: 3, borderRadius: '50%', background: 'rgba(255,255,255,0.4)' }}/>
        <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', fontWeight: 600, letterSpacing: 0.3 }}>
          🌐 {website}
        </Typography>
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CARD BACK
// ─────────────────────────────────────────────────────────────────────────────
const CardBack = ({ companyInfo, primary, accent }) => {
  const dark   = darken(primary, 0.30);
  const mid    = lighten(primary, 0.15);
  const bgCard = '#f8fafc';

  const HEADER_H = 56;
  const FOOTER_H = 54;

  const website = (companyInfo.website || `www.${(companyInfo.name||'hospital').toLowerCase().replace(/\s+/g,'')}hospital.com`).replace(/https?:\/\//,'');

  return (
    <Box sx={{
      width: CW, height: CH,
      position: 'relative', overflow: 'hidden',
      borderRadius: '14px',
      background: bgCard,
      fontFamily: '"Segoe UI", "Helvetica Neue", Arial, sans-serif',
    }}>
      {/* Background grid */}
      <Box sx={{
        position: 'absolute', inset: 0, zIndex: 0, opacity: 0.03,
        backgroundImage: `repeating-linear-gradient(0deg, ${primary} 0, ${primary} 1px, transparent 1px, transparent 28px),
                          repeating-linear-gradient(90deg, ${primary} 0, ${primary} 1px, transparent 1px, transparent 28px)`,
      }}/>

      {/* ── HEADER ── */}
      <Box sx={{
        position: 'absolute', top: 0, left: 0, right: 0, height: HEADER_H, zIndex: 4,
        background: `linear-gradient(130deg, ${dark} 0%, ${primary} 55%, ${mid} 100%)`,
        overflow: 'hidden',
      }}>
        {[0,1,2,3,4,5].map(i => (
          <Box key={i} sx={{
            position: 'absolute', top: -20, left: `${60 + i * 55}px`,
            width: '2px', height: '140px',
            background: 'rgba(255,255,255,0.06)',
            transform: 'rotate(25deg)',
          }}/>
        ))}
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{
            fontSize: 19, fontWeight: 900, color: 'white',
            letterSpacing: 8, textTransform: 'uppercase',
            textShadow: '0 2px 8px rgba(0,0,0,0.2)',
          }}>
            OUR SERVICES
          </Typography>
        </Box>
      </Box>

      {/* ── SERVICES GRID ── */}
      <Box sx={{
        position: 'absolute',
        top: HEADER_H + 6,
        left: 16, right: 16,
        bottom: FOOTER_H + 6,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: 'repeat(7, 1fr)',
        gap: '4px 12px',
        zIndex: 2,
      }}>
        {SERVICES.map(([l, r], i) => (
          <React.Fragment key={i}>
            {[l, r].map((svc, j) => (
              <Box key={j} sx={{
                display: 'flex', alignItems: 'center', gap: 1.2,
                px: 1.5, borderRadius: 1,
                background: 'white',
                border: `1px solid ${alpha(primary, 0.12)}`,
                boxShadow: `0 1px 3px ${alpha(primary, 0.06)}`,
              }}>
                <Box sx={{
                  width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                  background: j === 0 ? primary : accent,
                }}/>
                <Typography sx={{ fontSize: 11, color: '#1a1a2e', fontWeight: 600, lineHeight: 1.2 }}>
                  {svc}
                </Typography>
              </Box>
            ))}
          </React.Fragment>
        ))}
      </Box>

      {/* ── FOOTER / EMERGENCY ── */}
      <Box sx={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: FOOTER_H, zIndex: 4,
        background: `linear-gradient(130deg, ${dark} 0%, ${primary} 55%, ${mid} 100%)`,
        overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'stretch', px: 3, pt: '3px' }}>
          {/* LEFT: Emergency phone */}
          <Box sx={{
            flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center',
            borderRight: '1px solid rgba(255,255,255,0.2)', pr: 2.5,
          }}>
            <Typography sx={{ fontSize: 7.5, color: 'rgba(255,255,255,0.72)', fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', mb: 0.5 }}>
              ✆ Accident &amp; Emergency
            </Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 900, color: 'white', letterSpacing: 0.3, lineHeight: 1.2 }}>
              {companyInfo.phone || '0202369814 / 0245111940'}
            </Typography>
          </Box>

          {/* RIGHT: GPS + website */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', pl: 2.5 }}>
            <Typography sx={{ fontSize: 7.5, color: 'rgba(255,255,255,0.72)', fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', mb: 0.5 }}>
              ⬤ GPS Address
            </Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: 'white', lineHeight: 1.2, wordBreak: 'break-all' }}>
              {companyInfo.digital_add || 'N/A'}
            </Typography>
            <Typography sx={{ fontSize: 8, color: 'rgba(255,255,255,0.72)', mt: 0.3 }}>
              {website}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PRINT HTML — 85.6×54mm landscape, print-safe
// Uses: position:absolute, inline-block, table (no flex/grid)
// ─────────────────────────────────────────────────────────────────────────────
const buildPrintHTML = (patient, companyInfo, primary, accent, insurancePartners = []) => {
  const dark   = darken(primary, 0.30);
  const mid    = lighten(primary, 0.15);
  const bgCard = '#f8fafc';
  const website = (companyInfo.website || `www.${(companyInfo.name||'hospital').toLowerCase().replace(/\s+/g,'')}hospital.com`).replace(/https?:\/\//,'');
  const initials = (patient?.fullName || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  const a = (hex,op) => {
    const r=parseInt(hex.slice(1,3),16),g=parseInt(hex.slice(3,5),16),b=parseInt(hex.slice(5,7),16);
    return `rgba(${r},${g},${b},${op})`;
  };

  const W = 85.6, H = 54;

  // Heights in mm — defined first so SVG coords can reference TH
  const TH = 13;    // top header
  const FH = 3.5;   // front footer
  const BTH = 10.5; // back top header
  const BFH = 10;   // back footer/emergency

  // Print-safe header SVG (mm coords)
  // Initials circle in header
  const initCx = 9.5, initCy = TH / 2, initR = 4.5;
  const avatarSVG = `
    <circle cx="${initCx}" cy="${initCy}" r="${initR + 0.5}" fill="rgba(255,255,255,0.2)" stroke="rgba(255,255,255,0.55)" stroke-width="0.4"/>
    <circle cx="${initCx}" cy="${initCy}" r="${initR}" fill="${a(primary,0.3)}"/>
    <text x="${initCx}" y="${initCy}" text-anchor="middle" dominant-baseline="central"
      font-family="'Segoe UI',Arial,sans-serif" font-size="5" font-weight="900"
      fill="white" letter-spacing="-0.2">${initials}</text>`;

  // Logo — right side, no white background, sized to fill what the white circle used to fill
  const lgoCx = W - 7.5, lgoCy = TH / 2, lgoR = 6.2;
  const logoCircle = companyInfo.logo ? `
    <foreignObject x="${lgoCx - lgoR}" y="${lgoCy - lgoR}"
      width="${lgoR * 2}" height="${lgoR * 2}">
      <img xmlns="http://www.w3.org/1999/xhtml" src="${companyInfo.logo}"
        style="width:100%;height:100%;object-fit:contain;display:block;"/>
    </foreignObject>` : `
    <text x="${lgoCx}" y="${lgoCy}" text-anchor="middle" dominant-baseline="central"
      font-family="Arial,sans-serif" font-size="10" font-weight="900" fill="white">+</text>`;

  // Insurance list — build paired rows
  const insRows = (() => {
    const partners = insurancePartners.slice(0, 20);
    const pairs = [];
    for (let i = 0; i < partners.length; i += 2) {
      const left = partners[i];
      const right = partners[i+1];
      pairs.push(`
        <tr>
          <td style="padding:0 0.5mm 0.05mm 0; vertical-align:middle; width:50%; overflow:hidden; white-space:nowrap; max-width:21mm; line-height:1;">
            <span style="display:inline-block;width:0.9mm;height:0.9mm;border-radius:50%;background:${primary};vertical-align:middle;margin-right:0.4mm;"></span>
            <span style="font-size:5pt;color:#2a2a3e;font-weight:700;line-height:1.15;white-space:nowrap;">${left.name}</span>
          </td>
          ${right ? `<td style="padding:0 0.5mm 0.05mm 0; vertical-align:middle; width:50%; overflow:hidden; white-space:nowrap; max-width:21mm; line-height:1;">
            <span style="display:inline-block;width:0.9mm;height:0.9mm;border-radius:50%;background:${accent};vertical-align:middle;margin-right:0.4mm;"></span>
            <span style="font-size:5pt;color:#2a2a3e;font-weight:700;line-height:1.15;white-space:nowrap;">${right.name}</span>
          </td>` : '<td></td>'}
        </tr>`);
    }
    return pairs.join('');
  })();

  // Services table rows
  const svcRows = SERVICES.map(([l, r]) => `
    <tr>
      <td style="padding:0.2mm 1mm; font-size:6pt; color:#1a1a2e; font-weight:600; vertical-align:middle;
                 border:0.2mm solid ${a(primary,0.14)}; background:white; width:50%;">
        <span style="display:inline-block;width:1.3mm;height:1.3mm;border-radius:50%;background:${primary};vertical-align:middle;margin-right:0.7mm;"></span>${l}
      </td>
      <td style="padding:0.2mm 1mm; font-size:6pt; color:#1a1a2e; font-weight:600; vertical-align:middle;
                 border:0.2mm solid ${a(primary,0.14)}; background:white; width:50%;">
        <span style="display:inline-block;width:1.3mm;height:1.3mm;border-radius:50%;background:${accent};vertical-align:middle;margin-right:0.7mm;"></span>${r}
      </td>
    </tr>`).join('');

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8"/>
<title>ID Card – ${patient.fullName}</title>
<style>
  @page { size:85.6mm 54mm; margin:0; }
  html,body { width:85.6mm; margin:0; padding:0; font-family:'Segoe UI','Helvetica Neue',Arial,sans-serif; }
  * { box-sizing:border-box; -webkit-print-color-adjust:exact !important; print-color-adjust:exact !important; }

  .card      { width:85.6mm; height:54mm; position:relative; overflow:hidden; background:${bgCard}; }
  .card-back { page-break-before:always; }

  /* Header */
  .hdr {
    position:absolute; top:0; left:0; right:0; height:${TH}mm;
    background:${primary};
    overflow:hidden;
  }
  /* Header SVG fills the header */
  .hdr svg { position:absolute; top:0; left:0; }

  /* Lanyard removed */

  /* Hospital name — to the right of initials circle */
  .hosp-row { position:absolute; top:2mm; left:17mm; z-index:8; }
  .hosp-name { font-size:10.5pt; font-weight:800; color:white; line-height:1.2; }
  .hosp-sub  { font-size:4.5pt; color:rgba(255,255,255,0.75); letter-spacing:0.9px; text-transform:uppercase; }

  /* Accent bottom line on header — removed */
  .hdr-line { display:none; }

  /* Mid content */
  .left-col  { position:absolute; top:${TH + 0.8}mm; left:3.5mm; width:44mm; bottom:${FH + 0.8}mm; overflow:hidden; z-index:5; }
  .right-col { position:absolute; top:${TH + 1}mm; left:49mm; right:2.5mm; bottom:${FH + 0.8}mm; overflow:hidden; z-index:5; display:flex; flex-direction:column; justify-content:space-between; padding:0.5mm 0 3mm 0; }

  /* Divider */
  .divider { position:absolute; top:${TH + 1.5}mm; left:47mm; width:0.3mm; bottom:${FH + 1.5}mm; background:${a(primary, 0.15)}; z-index:5; }

  /* Patient name */
  .pt-name { font-size:7pt; font-weight:900; color:${dark}; text-transform:uppercase; line-height:1.05; letter-spacing:0.2px; margin:0 0 0.15mm 0; }
  .pt-role { font-size:3.5pt; color:${accent}; font-weight:800; letter-spacing:2.2px; text-transform:uppercase; margin:0 0 0.15mm 0; }
  .rule-line { width:100%; height:0.3mm; background:${primary}; margin-bottom:0.3mm; border-radius:0.2mm; }

  /* ID Badge */
  .id-badge {
    display:inline-block; padding:0.35mm 1.6mm; border-radius:0.8mm;
    background:${primary}; margin-bottom:0.4mm;
    box-shadow:0 1mm 3mm ${a(primary,0.38)};
  }
  .id-dot  { display:inline-block; width:1.3mm; height:1.3mm; border-radius:50%; background:rgba(255,255,255,0.55); vertical-align:middle; margin-right:0.7mm; }
  .id-text { font-size:9pt; font-weight:900; color:white; letter-spacing:1.5px; text-transform:uppercase; vertical-align:middle; }

  /* Insurance */
  .ins-title { font-size:3.5pt; color:${primary}; font-weight:900; text-transform:uppercase; letter-spacing:0.8px; display:block; margin-bottom:0.25mm; }
  .ins-table { border-collapse:collapse; width:100%; line-height:1.1; }
  .ins-table tr { line-height:1.1; }
  .ins-table td { padding:0 0.5mm 0.15mm 0; vertical-align:middle; width:50%; overflow:hidden; white-space:nowrap; max-width:21mm; line-height:1.1; }

  /* Info rows — right column (biodata) */
  .info-lbl { display:block; font-size:4.5pt; color:${a(primary,0.85)}; font-weight:800; text-transform:uppercase; letter-spacing:0.9px; line-height:1; margin-bottom:0.2mm; }
  .info-val { display:block; font-size:7.8pt; color:#1a1a2e; font-weight:800; line-height:1.25; margin-bottom:0; overflow:hidden; white-space:nowrap; text-overflow:ellipsis; }
  .info-val-wrap { display:block; font-size:7.2pt; color:#1a1a2e; font-weight:800; line-height:1.2; margin-bottom:0; white-space:normal; }
  .web-line { font-size:3.5pt; color:${a(primary,0.6)}; font-weight:600; display:block; margin-top:0.5mm; }

  /* Front footer — email bar */
  .front-footer {
    position:absolute; bottom:0; left:0; right:0; height:${FH}mm; z-index:4;
    background:${primary};
    display:table; width:100%;
  }
  .front-footer-inner { display:table-cell; vertical-align:middle; text-align:center; }
  .footer-email-text { font-size:6pt; color:rgba(255,255,255,0.9); font-weight:700; letter-spacing:0.3px; }
  .footer-web-text  { font-size:6pt; color:rgba(255,255,255,0.75); font-weight:600; letter-spacing:0.3px; margin-left:2mm; }

  /* ── BACK ── */
  .back-hdr {
    position:absolute; top:0; left:0; right:0; height:${BTH}mm; z-index:4;
    background:${primary}; overflow:hidden;
  }
  .back-hdr-line { display:none; }
  .back-footer-line { display:none; }  .svc-title-wrap { position:absolute; inset:0; display:table; width:100%; height:${BTH}mm; }
  .svc-title-cell { display:table-cell; vertical-align:middle; text-align:center; }
  .svc-title-text { font-size:9.5pt; font-weight:900; color:white; letter-spacing:5px; text-transform:uppercase; }

  .svc-area { position:absolute; top:${BTH+0.8}mm; bottom:${BFH+0.8}mm; left:3mm; right:3mm; overflow:hidden; z-index:2; }
  .svc-table { border-collapse:separate; border-spacing:0.5mm; width:100%; height:100%; table-layout:fixed; }
  .svc-table td { font-size:6pt; color:#1a1a2e; font-weight:600; padding:0.1mm 1mm; border:0.2mm solid ${a(primary,0.14)}; background:white; vertical-align:middle; }

  /* Back footer — emergency */
  .back-footer {
    position:absolute; bottom:0; left:0; right:0; height:${BFH}mm; z-index:4;
    background:${primary}; overflow:hidden;
  }
  .back-footer-line { display:none; }
  .emerg-table { display:table; width:100%; height:${BFH}mm; }
  .emerg-left  { display:table-cell; width:50%; vertical-align:middle; padding:1mm 2.5mm 1mm 3mm; border-right:0.3mm solid rgba(255,255,255,0.2); }
  .emerg-right { display:table-cell; width:50%; vertical-align:middle; padding:1mm 2.5mm; }
  .emerg-lbl   { font-size:3.8pt; color:rgba(255,255,255,0.75); font-weight:700; letter-spacing:0.5px; text-transform:uppercase; display:block; margin-bottom:0.5mm; }
  .emerg-val   { font-size:7.5pt; font-weight:900; color:white; display:block; line-height:1.2; }
  .emerg-val-sm{ font-size:5.5pt; font-weight:800; color:white; display:block; line-height:1.25; word-break:break-all; }
  .emerg-sub   { font-size:3.8pt; color:rgba(255,255,255,0.72); display:block; margin-top:0.4mm; }
</style>
</head>
<body>

<!-- ══════════════════════════════════════════════════ FRONT CARD -->
<div class="card">

  <!-- HEADER — SVG for gradient + circles -->
  <div class="hdr">
    <svg width="85.6mm" height="${TH}mm" viewBox="0 0 ${W} ${TH}" preserveAspectRatio="none">
      <!-- Gradient header background -->
      <defs>
        <linearGradient id="hdrGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stop-color="${dark}"/>
          <stop offset="55%"  stop-color="${primary}"/>
          <stop offset="100%" stop-color="${mid}"/>
        </linearGradient>
      </defs>
      <rect width="${W}" height="${TH}" fill="url(#hdrGrad)"/>
      <!-- Decorative diagonal lines -->
      <line x1="42" y1="-5" x2="32" y2="${TH+5}" stroke="rgba(255,255,255,0.18)" stroke-width="1.8"/>
      <line x1="54" y1="-5" x2="44" y2="${TH+5}" stroke="rgba(255,255,255,0.14)" stroke-width="1.8"/>
      <line x1="66" y1="-5" x2="56" y2="${TH+5}" stroke="rgba(255,255,255,0.10)" stroke-width="1.8"/>
      <line x1="78" y1="-5" x2="68" y2="${TH+5}" stroke="rgba(255,255,255,0.07)" stroke-width="1.8"/>
      <line x1="90" y1="-5" x2="80" y2="${TH+5}" stroke="rgba(255,255,255,0.05)" stroke-width="1.8"/>
      <!-- Initials circle -->
      ${avatarSVG}
      <!-- Logo circle -->
      ${logoCircle}
    </svg>
    <div class="hdr-line"></div>
  </div>

  <!-- HOSPITAL NAME -->
  <div class="hosp-row">
    <div class="hosp-name">${companyInfo.name || 'HOSPITAL'}</div>
    <div class="hosp-sub">${companyInfo.mmda || 'Quality Healthcare'}</div>
  </div>

  <!-- LEFT COLUMN -->
  <div class="left-col">
    <div class="pt-name">${patient.fullName}</div>
    <div class="pt-role">P A T I E N T</div>
    <div class="rule-line"></div>
    <div class="id-badge">
      <span class="id-dot"></span>
      <span class="id-text">ID: ${patient.id}</span>
    </div>
    ${insurancePartners.length > 0 ? `
    <span class="ins-title">Insurance Partners</span>
    <table class="ins-table">
      ${insRows}
    </table>` : ''}
  </div>

  <!-- DIVIDER -->
  <div class="divider"></div>

  <!-- RIGHT COLUMN -->
  <div class="right-col">
    <div>
      <span class="info-lbl">Phone</span>
      <span class="info-val">${patient.tel || companyInfo.phone || '—'}</span>
    </div>
    <div>
      <span class="info-lbl">Insurance No</span>
      <span class="info-val">${patient.Insurance || 'N/A'}</span>
    </div>
    <div>
      <span class="info-lbl">Gender</span>
      <span class="info-val">${patient.gender || patient.sex || 'N/A'}</span>
    </div>
    <div>
      <span class="info-lbl">Address</span>
      <span class="info-val-wrap">${patient.address || patient.location || companyInfo.location || 'N/A'}</span>
    </div>
  </div>

  <!-- FRONT FOOTER — email + website -->
  <div class="front-footer">
    <div class="front-footer-inner">
      <span class="footer-email-text">&#9993; ${companyInfo.email || `info@${(companyInfo.name||'hospital').toLowerCase().replace(/\s+/g,'')}hospital.com`}</span>
      <span class="footer-web-text">&#127760; ${website}</span>
    </div>
  </div>
</div>

<!-- ══════════════════════════════════════════════════ BACK CARD -->
<div class="card card-back">

  <!-- BACK HEADER -->
  <div class="back-hdr">
    <svg width="85.6mm" height="${BTH}mm" viewBox="0 0 ${W} ${BTH}" preserveAspectRatio="none">
      <defs>
        <linearGradient id="bhdrGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stop-color="${dark}"/>
          <stop offset="55%"  stop-color="${primary}"/>
          <stop offset="100%" stop-color="${mid}"/>
        </linearGradient>
      </defs>
      <rect width="${W}" height="${BTH}" fill="url(#bhdrGrad)"/>
      <line x1="42" y1="-3" x2="35" y2="${BTH+3}" stroke="rgba(255,255,255,0.18)" stroke-width="1.8"/>
      <line x1="54" y1="-3" x2="47" y2="${BTH+3}" stroke="rgba(255,255,255,0.14)" stroke-width="1.8"/>
      <line x1="66" y1="-3" x2="59" y2="${BTH+3}" stroke="rgba(255,255,255,0.10)" stroke-width="1.8"/>
      <line x1="78" y1="-3" x2="71" y2="${BTH+3}" stroke="rgba(255,255,255,0.07)" stroke-width="1.8"/>
      <line x1="90" y1="-3" x2="83" y2="${BTH+3}" stroke="rgba(255,255,255,0.05)" stroke-width="1.8"/>
    </svg>
    <div class="back-hdr-line"></div>
    <div class="svc-title-wrap">
      <div class="svc-title-cell">
        <span class="svc-title-text">OUR SERVICES</span>
      </div>
    </div>
  </div>

  <!-- SERVICES GRID -->
  <div class="svc-area">
    <table class="svc-table" style="height:100%;">
      ${svcRows}
    </table>
  </div>

  <!-- BACK FOOTER / EMERGENCY -->
  <div class="back-footer">
    <div class="back-footer-line"></div>
    <div class="emerg-table">
      <div class="emerg-left">
        <span class="emerg-lbl">&#9742; Accident &amp; Emergency</span>
        <span class="emerg-val">${companyInfo.phone || '0202369814 / 0245111940'}</span>
      </div>
      <div class="emerg-right">
        <span class="emerg-lbl">&#9679; GPS Address</span>
        <span class="emerg-val-sm">${companyInfo.digital_add || 'N/A'}</span>
        <span class="emerg-sub">${website}</span>
      </div>
    </div>
  </div>
</div>

</body>
</html>`;
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN DIALOG
// ─────────────────────────────────────────────────────────────────────────────
const PatientIDCard = ({ open, onClose, patient, companyInfo }) => {
  const [showBack,          setShowBack]          = useState(false);
  const [colors,            setColors]            = useState({ primary: FALLBACK_PRIMARY, accent: FALLBACK_ACCENT });
  const [extracting,        setExtracting]        = useState(false);
  const [insurancePartners, setInsurancePartners] = useState([]);

  useEffect(() => {
    if (!open) return;
    setShowBack(false);
    setExtracting(true);

    fetch(GET_CARD_COLORS_URL)
      .then(r => r.json())
      .then(data => {
        const c = data?.data;
        if (c?.primary_color && c?.accent_color) {
          setColors({ primary: c.primary_color, accent: c.accent_color });
          setExtracting(false);
        } else {
          const src = companyInfo?.logo;
          if (!src) { setColors({ primary: FALLBACK_PRIMARY, accent: FALLBACK_ACCENT }); setExtracting(false); return; }
          extractColorsFromImage(src).then(setColors).finally(() => setExtracting(false));
        }
      })
      .catch(() => {
        const src = companyInfo?.logo;
        if (!src) { setColors({ primary: FALLBACK_PRIMARY, accent: FALLBACK_ACCENT }); setExtracting(false); return; }
        extractColorsFromImage(src).then(setColors).finally(() => setExtracting(false));
      });

    fetch(GET_INSURANCE_URL)
      .then(r => r.json())
      .then(data => { if (data?.success) setInsurancePartners(data.data || []); })
      .catch(() => {});
  }, [open, companyInfo?.logo]);

  if (!patient || !companyInfo) return null;

  const { primary, accent } = colors;

  const handlePrint = async () => {
    const user      = JSON.parse(localStorage.getItem('user') || '{}');
    const from_user = user?.username || user?.name || 'admin';

    let cardAmount     = 0;
    let alreadyCharged = false;

    try {
      const [amtRes, chkRes] = await Promise.all([
        fetch(GET_ID_CARD_AMOUNT_URL),
        fetch(CHECK_ID_CARD_CHARGE_URL + '?patientId=' + patient.id),
      ]);
      const amtData = await amtRes.json();
      const chkData = await chkRes.json();
      if (amtData?.success && amtData?.data?.amount != null)
        cardAmount = parseFloat(amtData.data.amount);
      if (chkData?.success)
        alreadyCharged = Boolean(chkData.alreadyCharged);
    } catch (_) {}

    let html, confirmText;
    if (alreadyCharged) {
      html = `<div style="text-align:left;font-size:14px;line-height:1.8">
        <b>${patient.fullName}</b> &nbsp;·&nbsp; ID: <b>${patient.id}</b><br/><br/>
        <span style="color:#2e7d32;font-size:13px">✓ ID card fee was already charged today.</span><br/>
        <span style="color:#555;font-size:12px">No additional charge will be applied.</span>
      </div>`;
      confirmText = 'Print Card';
    } else if (cardAmount > 0) {
      html = `<div style="text-align:left;font-size:14px;line-height:1.8">
        <b>${patient.fullName}</b> &nbsp;·&nbsp; ID: <b>${patient.id}</b><br/><br/>
        <b style="color:#e07b39">GHS ${cardAmount.toFixed(2)}</b> will be charged to the patient's bill.
      </div>`;
      confirmText = 'Yes, charge & print';
    } else {
      html = `<div style="text-align:left;font-size:14px;line-height:1.8">
        <b>${patient.fullName}</b> &nbsp;·&nbsp; ID: <b>${patient.id}</b><br/><br/>
        <span style="color:#2e7d32"><b>No charge</b> — ID card is currently free.</span>
      </div>`;
      confirmText = 'Yes, print card';
    }

    const result = await Swal.fire({
      title: 'Print ID Card?', html, icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#1a6b8a', cancelButtonColor: '#aaa',
      confirmButtonText: confirmText, cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    if (!alreadyCharged) {
      try {
        await fetch(REQUEST_SERVICE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            patientId: patient.id, name: 'ID Card Printing',
            department: 'Records', charge: 'ID Card',
            amount: cardAmount, doc_name: from_user, from_user,
            dat: new Date().toISOString().split('T')[0],
          }),
        });
      } catch (_) {}
    }

    const win = window.open('', '_blank', 'width=960,height=720');
    win.document.write(buildPrintHTML(patient, companyInfo, primary, accent, insurancePartners));
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 900);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false}
      PaperProps={{ sx: {
        background: 'linear-gradient(160deg,#0a1628 0%,#0d2040 100%)',
        borderRadius: 4, overflow: 'hidden',
        boxShadow: '0 40px 100px rgba(0,0,0,0.75)',
        border: '1px solid rgba(255,255,255,0.05)',
      }}}
    >
      <Box sx={{ p: 3.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5, minWidth: 740 }}>

        {/* Header row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
          <Box>
            <Typography sx={{ color: 'white', fontWeight: 800, fontSize: 19, letterSpacing: 0.3 }}>Patient ID Card</Typography>
            <Typography sx={{ color: '#475569', fontSize: 12, mt: 0.3 }}>
              {patient.fullName} &nbsp;·&nbsp; ID {patient.id}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            {extracting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={13} sx={{ color: '#64748b' }}/>
                <Typography sx={{ fontSize: 10, color: '#64748b' }}>Detecting colors…</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: primary, border: '2px solid rgba(255,255,255,0.18)', boxShadow: `0 0 10px ${alpha(primary,0.65)}` }}/>
                <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: accent,  border: '2px solid rgba(255,255,255,0.18)', boxShadow: `0 0 10px ${alpha(accent,0.65)}` }}/>
                <Typography sx={{ fontSize: 10, color: '#475569', ml: 0.4 }}>Logo colors</Typography>
              </Box>
            )}
            <Chip label="Landscape · CR80 · 85.6×54mm" size="small"
              sx={{ backgroundColor: 'rgba(255,255,255,0.05)', color: '#64748b', fontSize: 10, border: '1px solid rgba(255,255,255,0.07)' }}/>
            <IconButton onClick={onClose} sx={{ color: '#475569', '&:hover': { color: 'white' } }}>
              <CloseIcon fontSize="small"/>
            </IconButton>
          </Box>
        </Box>

        {/* Front / Back toggle */}
        <Box sx={{ display: 'flex', borderRadius: 2, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
          {['Front', 'Back'].map(side => (
            <Box key={side} onClick={() => setShowBack(side === 'Back')} sx={{
              px: 3.5, py: 1, cursor: 'pointer',
              backgroundColor: (showBack ? side === 'Back' : side === 'Front') ? primary : 'transparent',
              color:           (showBack ? side === 'Back' : side === 'Front') ? 'white' : '#64748b',
              fontWeight: 700, fontSize: 12, transition: 'all 0.25s',
              '&:hover': { backgroundColor: (showBack ? side === 'Back' : side === 'Front') ? primary : 'rgba(255,255,255,0.04)' },
            }}>{side}</Box>
          ))}
        </Box>

        {/* Card preview */}
        <Box sx={{
          opacity: extracting ? 0.3 : 1, transition: 'opacity 0.3s',
          pointerEvents: extracting ? 'none' : 'auto',
          borderRadius: '16px', overflow: 'hidden',
          boxShadow: `0 28px 70px ${alpha(primary, 0.4)}, 0 8px 24px rgba(0,0,0,0.5)`,
        }}>
          {showBack
            ? <CardBack companyInfo={companyInfo} primary={primary} accent={accent}/>
            : <CardFront patient={patient} companyInfo={companyInfo} primary={primary} accent={accent} insurancePartners={insurancePartners}/>
          }
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined"
            onClick={() => setShowBack(v => !v)}
            startIcon={showBack ? <FlipToFrontIcon/> : <FlipToBackIcon/>}
            sx={{
              color: '#94a3b8', borderColor: 'rgba(255,255,255,0.09)',
              textTransform: 'none', fontWeight: 600, borderRadius: 2,
              '&:hover': { borderColor: 'rgba(255,255,255,0.18)', backgroundColor: 'rgba(255,255,255,0.04)' },
            }}>
            {showBack ? 'View Front' : 'View Back'}
          </Button>
          <Button variant="contained"
            startIcon={extracting ? <CircularProgress size={15} sx={{ color: 'white' }}/> : <PrintIcon/>}
            onClick={handlePrint} disabled={extracting}
            sx={{
              background: `linear-gradient(135deg, ${darken(primary,0.28)}, ${primary} 55%, ${accent})`,
              fontWeight: 700, textTransform: 'none', px: 3.5, borderRadius: 2,
              boxShadow: `0 4px 18px ${alpha(primary, 0.5)}`,
              '&:hover': { boxShadow: `0 6px 24px ${alpha(primary, 0.65)}` },
              '&:disabled': { background: 'rgba(255,255,255,0.05)', color: '#475569' },
            }}>
            {extracting ? 'Reading colors…' : 'Print ID Card'}
          </Button>
        </Box>

        <Typography sx={{ color: '#2d3f55', fontSize: 10, letterSpacing: 0.3 }}>
          Clean design · Landscape CR80 (85.6 × 54 mm) · Magicard 300 · Colors extracted from logo
        </Typography>
      </Box>
    </Dialog>
  );
};

export default PatientIDCard;